/*
 * Copyright (c) 2021.
 * 2iPartners SàRL and/or its affiliates.
 * All rights reserved.
 *
 * 2iPartners SàRL
 * Route de Compois 24,
 * 1252 Meinier
 * Suisse
 *
 * tel : +41 22 735 97 07
 */

import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import {BAD_REQUEST, CONFLICT, FORBIDDEN, INTERNAL_SERVER_ERROR, NOT_FOUND, REQUEST_TIME_OUT, UNAUTHORIZED} from '../_enums/requests';

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///                              I18N LOCALES                                                                                          ///
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const EN = 'en';
export const FR = 'fr';

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///                              I18N KEYS                                                                                             ///
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


export const ANIMAL_NUMBER_DUPLICATE = 'ANIMAL_NUMBER_DUPLICATE';
export const AVAILABLE_SOON = 'AVAILABLE_SOON';
export const CREATE_SUCCESS = 'CREATE_SUCCESS';
export const FORGOT_PASSWORD_REQUEST_SEND = 'FORGOT_PASSWORD_REQUEST_SEND';
export const HISTORY = 'HISTORY';
export const INVOICE = 'INVOICE';
export const MY_ACCOUNT = 'MY_ACCOUNT';
export const NO_USER = 'NO_USER';
export const OLD_PASSWORD_INVALID = 'OLD_PASSWORD_INVALID';
export const ONE_PHONE_NUMBER_NEEDED = 'ONE_PHONE_NUMBER_NEEDED';
export const REMOVAL_IMPOSSIBLE = 'REMOVAL_IMPOSSIBLE';
export const RESET_PASSWORD_SUCCESS = 'RESET_PASSWORD_SUCCESS';
export const RP_PHONE_NEEDED = 'RP_PHONE_NEEDED';
export const UNKNOWN_ERROR = 'UNKNOWN_ERROR';
export const UPDATE_SUCCESS = 'UPDATE_SUCCESS';
export const VOUCHER = 'VOUCHER';
export const VOUCHER_INVOICE = 'VOUCHER_INVOICE';
export const WRONG_ID = 'WRONG_ID';


@Injectable()
export class I18nService {

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PROPERTIES                                                                                            ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private availableLocales = [EN, FR];

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              MAGICS METHODS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor(@Inject(LOCALE_ID) public locale: string) {
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PUBLIC METHODS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public get(key: string | number, params?: any, localization?: string): string {
    const l = this.getLocale(localization).toLowerCase();
    const messages = {};

    messages[ANIMAL_NUMBER_DUPLICATE] = {
      en: `This removal request already contains an animal with the number: ${params}.`,
      fr: `Cette demande contient déjà un animal avec le numéro: ${params}.`
    };

    messages[AVAILABLE_SOON] = {
      en: `Available soon.`,
      fr: `Bientôt disponible`
    };

    messages[BAD_REQUEST] = {
      en: `The syntax of the request is wrong. As a result, it could not be processed.`,
      fr: `La syntaxe de la requête est erronée. Par conséquent, celle-ci n'a pu être traitée.`
    };

    messages[CONFLICT] = {
      en: `The request can not be processed in the current state because it causes conflicts.`,
      fr: `La requête ne peut être traitée en l’état actuel, car elle occasionne des conflits.`
    };

    messages[CREATE_SUCCESS] = {
      en: `Created successfully.`,
      fr: `La création s'est bien passée.`
    };

    messages[FORBIDDEN] = {
      en: `You are not granted to run this request.`,
      fr: `Vous ne disposez pas des droits nécéssaires pour exécuter cette requête.`
    };

    messages[FORGOT_PASSWORD_REQUEST_SEND] = {
      en: `An email with a password reset link has been sent to you.`,
      fr: `Un email avec un lien de réinitialisation du mot de passe vous a été envoyé.`
    };

    messages[INTERNAL_SERVER_ERROR] = {
      en: `Internal server error.`,
      fr: `Erreur interne du serveur.`
    };

    messages[INVOICE] = {
      en: `Invoice`,
      fr: `Facture`
    };

    messages[HISTORY] = {
      en: `History`,
      fr: `Historique`
    };

    messages[MY_ACCOUNT] = {
      en: `My account`,
      fr: `Mon compte`
    };


    messages[NO_USER] = {
      en: `You do not have an internet account, necessary to continue this request. Call the switchboard to communicate your email address.`,
      fr: `Vous ne disposez pas d'un compte internet, nécessaire pour poursuivre cette demande. Appeler le standard pour communiquer votre adresse email.`
    };

    messages[NOT_FOUND] = {
      en: `All the resources needed to run the request could not be found.`,
      fr: `Toutes les ressources nécéssaires à l'exécution de la requête n'ont pu être trouvées.`
    };

    messages[OLD_PASSWORD_INVALID] = {
      en: `The old password is invalid.`,
      fr: `Votre ancien mot de passe n'est pas valide.`
    };

    messages[ONE_PHONE_NUMBER_NEEDED] = {
      en: `At least a phone number is needed.`,
      fr: `Le lieu de collecte doit avoir au moins un numéro de téléphone.`
    };

    messages[REMOVAL_IMPOSSIBLE] = {
      en: `You can't make this removal request from the web site.`,
      fr: `Vous ne pouvez pas effectuer cette demande d'enlèvement depuis le site.`
    };

    messages[REQUEST_TIME_OUT] = {
      en: `The time to complete this request has been exceeded. Vou must resume from the beginning.`,
      fr: `Le temps imparti pour l'exécution de la requête a été dépassé. Veuillez reprendre depuis le début.`
    };

    messages[RESET_PASSWORD_SUCCESS] = {
      en: `Your password has been reset.`,
      fr: `Votre mot de passe a bien été réinitialisé.`
    };

    messages[RP_PHONE_NEEDED] = {
      en: `You must provide at least one phone number for the removal point.`,
      fr: `Vous devez renseigner au moins un numéro de téléphone pour le lieux de collecte.`
    };

    messages[UNAUTHORIZED] = {
      en: `You must authenticate to be able to execute this request.`,
      fr: `Vous devez vous authentifier pour pouvoir exécuter cette requête.`
    };

    messages[UNKNOWN_ERROR] = {
      en: `An unknown error occurred.`,
      fr: `Une erreur inconnue est survenue.`
    };

    messages[UPDATE_SUCCESS] = {
      en: `Update successfully.`,
      fr: `La modification a bien été prise en charge.`
    };

    messages[VOUCHER] = {
      en: `Voucher`,
      fr: `Bon`
    };

    messages[VOUCHER_INVOICE] = {
      en: `Voucher_Invoice`,
      fr: `Bon_Facture`
    };

    messages[WRONG_ID] = {
      en: `Invalid username and password combination.`,
      fr: `Combinaison identifiant/ mot de passe invalide.`
    };

    return messages[key] && messages[key][l] ? messages[key][l] : messages[UNKNOWN_ERROR][l];
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PROTECTED METHODS                                                                                     ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  protected getLocale(localization?: string): string {
    if (localization) {
      return 0 > this.availableLocales.indexOf(localization.toLowerCase()) ? this.locale : localization;
    }

    return this.locale;
  }

}
